import { useState, useEffect, useRef } from "react";
import Loader from "../../components/Loader";
import { withTranslation, useTranslation } from "react-i18next";
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    GetDashboardLogListSearchUrl,
    GetDashboardLogListExcelUrl,
    dashboardGroupActionID,
} from "../../utils/constants";
import { useToast } from "../../components/toast";
import { axiosPost } from "../../utils/Axios";
import CommonPagination from "../../components/CommonPagination";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useFillList } from "../../components/fillList";
import { HeaderBlock } from "@colliers/colliers-b2c";
import axios from "axios";

const LIST_ITEMS_PER_PAGE = 10;
const DATE_FORMAT_INPUT = "YYYY-MM-DD";
const DATE_FORMAT_INPUT_WITH_TIME = "YYYY-MM-DD HH:mm:ss";
const DATE_FORMAT_FILTER = "DD-MMM-YYYY";
const DATE_FORMAT_API = "DD-MM-YYYY HH:mm:ss";
const DATE_FORMAT_DISPLAY = "DD MMM YYYY HH:mm:ss";
const EXCEL_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_ERROR_MSG = "Failed to generate excel";
const FETCH_ERROR_MSG = "Failed to fetch dashboard log: No data found.";
const READONLY_INPUT_SLOT_PROPS = { input: { readOnly: true } };
const SHRINK_LABEL_SLOT_PROPS = { inputLabel: { shrink: true } };

export const isEmpty = (value) => value == null || String(value).trim() === "";

export const getDateRange = (dateRange, fromDate, toDate) => {
    if (isEmpty(dateRange)) return "";
    return String(dateRange).includes("Between") ? `${fromDate}|${toDate}` : dateRange;
};

export const getDashboardGroupID = (dashboardGroupID) => {
    if (
        isEmpty(dashboardGroupID) ||
        dashboardGroupID === "select" ||
        dashboardGroupID === "0" ||
        dashboardGroupID === 0
    ) {
        return null;
    }
    return dashboardGroupID;
};

export const buildSearchParam = (filters) => ({
    dateRange: getDateRange(
        filters?.dateRange,
        filters?.fromDateFormatted,
        filters?.toDateFormatted
    ),
    dashboard: isEmpty(filters?.dashboard) ? "" : String(filters.dashboard).trim(),
    name: isEmpty(filters?.userName) ? "" : String(filters.userName).trim(),
    dashboardGroupID: getDashboardGroupID(filters?.dashboardGroupID),
});

export const extractDownloadFileName = (disposition) => {
    const defaultFileName = `CRD_Dashboard_Log_${moment().format("YYYY-MM-DD")}.xlsx`;
    if (!disposition) return defaultFileName;
    const filenameStarMatch = /filename\*=UTF-8''([^;]+)/.exec(disposition);
    if (filenameStarMatch?.[1]) {
        return decodeURIComponent(filenameStarMatch[1]);
    }
    const filenameMatch = /filename="?([^";]+)/.exec(disposition);
    return filenameMatch?.[1] || defaultFileName;
};

export const formatViewDate = (viewDate) => {
    if (isEmpty(viewDate)) return "Invalid Date";
    const trimmed = String(viewDate).replace(/^GMT\s+/i, "").trim();
    const parsed = moment(
        trimmed,
        [DATE_FORMAT_API, DATE_FORMAT_DISPLAY, moment.ISO_8601],
        true
    );
    return parsed.isValid() ? parsed.format(DATE_FORMAT_DISPLAY) : "Invalid Date";
};

const getDashboardLogRowKey = (item) =>
    [
        item?.dashboardID ?? "",
        item?.viewDate ?? "",
        item?.user ?? "",
        item?.userType ?? "",
        item?.dashboard ?? "",
        item?.dashboardGroup ?? "",
    ].join("|");

const triggerBlobDownload = (blobData, fileName) => {
    const blob = new Blob([blobData], { type: EXCEL_MIME_TYPE });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL?.(objectUrl), 100);
};

const DashboardLog = (_props) => {
    const navigate = useNavigate();
    const { ToastComponent, showToast } = useToast();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [userName, setUserName] = useState("");
    const [dashboard, setDashboard] = useState("");
    const [DashboardGroupID, setDashboardGroupID] = useState("");
    const groupFillList = useFillList(dashboardGroupActionID, "", "");
    const DashboardGroupFillList = Array.isArray(groupFillList?.data)
        ? groupFillList.data
        : [];

    const [filteredDashboardLog, setFilteredDashboardLog] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [dateRange, setDateRange] = useState("");
    const [showDatePickers, setShowDatePickers] = useState(false);

    const fromDateRef = useRef("");
    const toDateRef = useRef("");

    const startIndex = (currentPage - 1) * LIST_ITEMS_PER_PAGE;
    const paginatedDashboardLog = filteredDashboardLog?.slice(
        startIndex,
        startIndex + LIST_ITEMS_PER_PAGE
    );
    const totalItems = filteredDashboardLog?.length ?? 0;

    const getCurrentFilters = () => ({
        dateRange,
        fromDateFormatted: fromDateRef.current,
        toDateFormatted: toDateRef.current,
        dashboard,
        userName,
        dashboardGroupID: DashboardGroupID,
    });

    const getDashboardLogList = async (overrideFilters) => {
        setIsLoading(true);
        try {
            const activeFilters = overrideFilters ?? getCurrentFilters();
            const dashboardLogSearchParam = buildSearchParam(activeFilters);
            const response = await axiosPost(
                GetDashboardLogListSearchUrl,
                dashboardLogSearchParam
            );
            if (response) {
                setFilteredDashboardLog(Array.isArray(response) ? response : []);
            } else {
                showToast("error", FETCH_ERROR_MSG);
                console.error(FETCH_ERROR_MSG);
            }
        } catch (error) {
            console.error("Error fetching dashboard log:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const searchBtnCall = async () => {
        setCurrentPage(1);
        await getDashboardLogList();
    };

    const resetSearch = async () => {
        fromDateRef.current = "";
        toDateRef.current = "";
        setFromDate("");
        setToDate("");
        setDateRange("");
        setShowDatePickers(false);
        setUserName("");
        setDashboard("");
        setDashboardGroupID("");
        setCurrentPage(1);
        await getDashboardLogList({});
    };

    const exportBtnCall = async () => {
        setIsLoading(true);
        try {
            const dashboardLogSearchParam = buildSearchParam(getCurrentFilters());
            const response = await axios.post(
                GetDashboardLogListExcelUrl,
                dashboardLogSearchParam,
                { responseType: "blob" }
            );
            if (response) {
                const fileName = extractDownloadFileName(
                    response.headers?.["content-disposition"]
                );
                triggerBlobDownload(response.data, fileName);
            } else {
                showToast("error", EXCEL_ERROR_MSG);
            }
        } catch (error) {
            showToast("error", EXCEL_ERROR_MSG);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const userInfo = localStorage.getItem("userInfo");
        if (userInfo == null) {
            navigate("/login/KeyStone_Login");
            return;
        }
        void getDashboardLogList();
    }, []);

    const updateDateRangeFromRefs = () => {
        if (fromDateRef.current && !toDateRef.current) {
            setDateRange(`greater than ${fromDateRef.current}`);
        } else if (toDateRef.current && !fromDateRef.current) {
            setDateRange(`less than ${toDateRef.current}`);
        } else if (fromDateRef.current && toDateRef.current) {
            setDateRange(`Between ${fromDateRef.current} and ${toDateRef.current}`);
        } else {
            setDateRange("");
        }
    };

    const applyDateSelection = (value, dateRef, setDate) => {
        if (isEmpty(value)) return;
        dateRef.current = moment(
            value,
            [DATE_FORMAT_INPUT, DATE_FORMAT_INPUT_WITH_TIME],
            true
        ).format(DATE_FORMAT_FILTER);
        setDate(value);
        updateDateRangeFromRefs();
    };

    return (
        <>
            {isLoading && <Loader />}
            <HeaderBlock
                left=""
                title={t("dashboard_log.dashboardLog")}
                leftCenterAlign="true"
                actionsCenterAlign="true"
            />

            <Box
                id="main"
                className="full-width padding-right-m padding-left-m padding-bottom-m padding-top-none logMain"
            >
                <Box className="pagetitle main-area-top" textAlign="center">
                    <section className="section page-body">
                        <Box className="margin-top-m margin-bottom-none logFilterBar">
                            <Box className="text-align-left list-page-control-dashlog">
                                <TextField
                                    className="padding-top-s margin-bottom-s full-width"
                                    label={t("dashboard_log.dateRange")}
                                    value={dateRange}
                                    slotProps={READONLY_INPUT_SLOT_PROPS}
                                    variant="outlined"
                                    onClick={() => setShowDatePickers(true)}
                                    placeholder={t("dashboard_log.selectDate")}
                                />
                                {showDatePickers && (
                                    <Box className="full-width">
                                        <TextField
                                            className="margin-bottom-xs"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) =>
                                                applyDateSelection(
                                                    e.target.value,
                                                    fromDateRef,
                                                    setFromDate
                                                )
                                            }
                                            required
                                            variant="outlined"
                                            slotProps={SHRINK_LABEL_SLOT_PROPS}
                                        />
                                        <TextField
                                            className="margin-bottom-xs"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) =>
                                                applyDateSelection(
                                                    e.target.value,
                                                    toDateRef,
                                                    setToDate
                                                )
                                            }
                                            required
                                            variant="outlined"
                                            slotProps={SHRINK_LABEL_SLOT_PROPS}
                                        />
                                    </Box>
                                )}
                            </Box>
                            {/* Dashboard Group Filter */}
                            <TextField
                                className="padding-top-s list-page-control-dashlog margin-bottom-s text-align-left"
                                label={t("dashboard_log.dashboardGroup")}
                                onChange={(e) => setDashboardGroupID(e.target.value)}
                                value={DashboardGroupID}
                                select
                                required
                                variant="outlined"
                            >
                                <MenuItem value="0">
                                    {t("dashboard_log.selectDashboardGroup")}
                                </MenuItem>
                                {DashboardGroupFillList.map((data) => (
                                    <MenuItem key={data.code} value={data.code}>
                                        {data.description}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {/* Dashboard Name Filter */}
                            <TextField
                                className="padding-top-s list-page-control-dashlog margin-bottom-s"
                                label={t("dashboard_log.dashboard")}
                                placeholder={t("dashboard_log.dashboard")}
                                variant="outlined"
                                value={dashboard}
                                onChange={(e) => setDashboard(e.target.value)}
                                slotProps={SHRINK_LABEL_SLOT_PROPS}
                            />
                            {/* User Name Filter */}
                            <TextField
                                className="padding-top-s list-page-control-dashlog margin-bottom-s"
                                label={t("dashboard_log.userName")}
                                placeholder={t("dashboard_log.enterUserName")}
                                variant="outlined"
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                slotProps={SHRINK_LABEL_SLOT_PROPS}
                            />

                            {/* Action Buttons */}
                            <Button
                                className="margin-top-s logborder"
                                variant="contained"
                                color="primary"
                                onClick={searchBtnCall}
                            >
                                {t("dashboard_log.view")}
                            </Button>
                            <Button
                                className="margin-top-s logborder"
                                variant="contained"
                                color="primary"
                                onClick={resetSearch}
                            >
                                {t("common.reset")}
                            </Button>
                            <Button
                                variant="outlined"
                                className="margin-top-s logborder"
                                color="primary"
                                onClick={exportBtnCall}
                            >
                                {t("dashboard_log.download")}
                            </Button>
                            <Typography variant="body2" className="logfontsize">
                                {t("dashboard_log.total")} {totalItems}{" "}
                                {t("dashboard_log.recordsFound")}
                            </Typography>
                            <TableContainer className="margin-bottom-xl list-height padding-x-base padding-bottom-base padding-top-none shadow-0 border-light border-radius">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("dashboard_log.viewDate")}</TableCell>
                                            <TableCell>{t("dashboard_log.user")}</TableCell>
                                            <TableCell>{t("dashboard_log.userType")}</TableCell>
                                            <TableCell>{t("dashboard_log.dashboard")}</TableCell>
                                            <TableCell>{t("dashboard_log.group")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedDashboardLog?.length > 0 ? (
                                            paginatedDashboardLog.map((dashboardLog) => (
                                                <TableRow
                                                    key={getDashboardLogRowKey(dashboardLog)}
                                                >
                                                    <TableCell>
                                                        {formatViewDate(dashboardLog.viewDate)}
                                                    </TableCell>
                                                    <TableCell>{dashboardLog.user}</TableCell>
                                                    <TableCell>{dashboardLog.userType}</TableCell>
                                                    <TableCell className="logTableWrapCell">
                                                        {dashboardLog.dashboard}
                                                    </TableCell>
                                                    <TableCell className="logTableWrapCell">
                                                        {dashboardLog.dashboardGroup}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    {t("common.noRecordsFound")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                                <CommonPagination
                                    totalItems={totalItems}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            </TableContainer>
                        </Box>
                    </section>
                </Box>
            </Box>

            {ToastComponent}
        </>
    );
};

export default withTranslation()(DashboardLog);
